import { environment } from 'src/environments/environment';

/** Default square size for list / header avatars (matches CMS 32–50px display). */
export const AVATAR_THUMB_WIDTH = 64;

/**
 * Build absolute media URL from a relative path or absolute URL.
 * Does not add thumbnail params — use {@link resolveAvatarUrl} for avatars.
 */
export function resolveMediaUrl(
  pathOrUrl: string | null | undefined,
  root: string = environment.root
): string | null {
  const trimmed = String(pathOrUrl ?? '').trim();
  if (!trimmed) {
    return null;
  }
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('data:')) {
    return trimmed;
  }
  const base = root.endsWith('/') ? root : `${root}/`;
  const path = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
  return base + path;
}

/**
 * True when the URL is served by our API media mounts that honor `?w=` thumbnails.
 */
export function isResizableMediaUrl(url: string, root: string = environment.root): boolean {
  if (!url || url.startsWith('data:')) {
    return false;
  }
  try {
    const base = new URL(root.endsWith('/') ? root : `${root}/`);
    const u = new URL(url, base);
    if (u.origin !== base.origin) {
      return false;
    }
    const p = u.pathname.toLowerCase();
    return (
      p.startsWith('/uploads/') ||
      p.startsWith('/img/') ||
      p.startsWith('/public/')
    );
  } catch {
    return false;
  }
}

/**
 * Avatar / list thumbnail URL. Appends `?w=` so the API can return a cached square WebP
 * instead of the full original (A18). Falls back to the full URL if resize is unavailable.
 */
export function resolveAvatarUrl(
  pathOrUrl: string | null | undefined,
  width: number = AVATAR_THUMB_WIDTH,
  root: string = environment.root
): string | null {
  const url = resolveMediaUrl(pathOrUrl, root);
  if (!url) {
    return null;
  }
  if (!isResizableMediaUrl(url, root)) {
    return url;
  }
  const w = Math.max(16, Math.min(512, Math.round(Number(width) || AVATAR_THUMB_WIDTH)));
  try {
    const u = new URL(url);
    u.searchParams.set('w', String(w));
    return u.toString();
  } catch {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}w=${w}`;
  }
}
